/**
 * ISATS Portal — Secure Applications Repository (P0-1a refactored)
 *
 * Transparently encrypts PII before writing to Firestore, and decrypts
 * PII after reading — but the crypto now happens ON THE SERVER via
 * POST /api/pii/encrypt and POST /api/pii/decrypt. The client never
 * holds the DEK/HMAC keys (GET /api/pii-key was deleted in P0-1a after
 * the full security audit found it handed the master keys to any
 * authenticated account). The server verifies the caller's access to
 * each record (owner / workspace membership / platform operator)
 * before transforming its payload.
 *
 * Firestore reads/writes themselves still go through the client SDK
 * exactly as before, so Firestore rules keep governing the data path.
 */

import { getDocs, getDoc, updateDoc, setDoc, addDoc } from 'firebase/firestore';
import { auth } from './firebase';

const PII_COLLECTION = 'applications';
const TRANSFORM_CHUNK = 100;

async function piiTransform(
  kind: 'encrypt' | 'decrypt',
  items: Array<{ id?: string; data: any }>
): Promise<Array<{ id: string; data: any; error?: string }>> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('PII transform requires an authenticated user');
  }
  const token = await user.getIdToken();
  const out: Array<{ id: string; data: any; error?: string }> = [];
  for (let i = 0; i < items.length; i += TRANSFORM_CHUNK) {
    const chunk = items.slice(i, i + TRANSFORM_CHUNK);
    const res = await fetch(`/api/pii/${kind}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ collection: PII_COLLECTION, items: chunk }),
    });
    if (!res.ok) {
      throw new Error(`PII ${kind} failed: HTTP ${res.status}`);
    }
    const body = await res.json();
    const results = Array.isArray(body?.items) ? body.items : [];
    for (const r of results) {
      if (r && r.error) {
        throw new Error(`PII ${kind} refused for a record (${r.error}). The record may belong to another workspace.`);
      }
      out.push(r);
    }
  }
  if (out.length !== items.length) {
    throw new Error(`PII ${kind} returned an unexpected result count`);
  }
  return out;
}

/**
 * Wraps getDocs, decrypting PII fields on all retrieved documents
 * (server-side transform). Returns a compatible QuerySnapshot structure.
 */
export async function secureGetDocs(q: any): Promise<any> {
  const snap = await getDocs(q);
  if (snap.docs.length === 0) {
    return { ...snap, docs: [], forEach: (cb: (doc: any) => void) => snap.docs.forEach(cb) };
  }
  const results = await piiTransform(
    'decrypt',
    snap.docs.map((docSnap: any) => ({ id: docSnap.id, data: docSnap.data() }))
  );
  const byId = new Map(results.map((r) => [r.id, r.data]));
  const decryptedDocs = snap.docs.map((docSnap: any) => ({
    id: docSnap.id,
    ref: docSnap.ref,
    exists: () => true,
    data: () => byId.get(docSnap.id),
  }));
  return {
    ...snap,
    docs: decryptedDocs,
    forEach: (callback: (doc: any) => void) => decryptedDocs.forEach(callback),
  };
}

/**
 * Wraps getDoc, decrypting PII fields on the retrieved document
 * (server-side transform). Returns a compatible DocumentSnapshot structure.
 */
export async function secureGetDoc(docRef: any): Promise<any> {
  const snap = await getDoc(docRef);
  if (!snap.exists()) {
    return snap;
  }
  const results = await piiTransform('decrypt', [{ id: docRef.id, data: snap.data() }]);
  const decryptedData = results[0]?.data;
  return {
    id: snap.id,
    ref: snap.ref,
    exists: () => true,
    data: () => decryptedData,
  };
}

/**
 * Wraps updateDoc, encrypting all updated PII fields and generating
 * blind indexes (server-side transform).
 */
export async function secureUpdateDoc(docRef: any, data: any): Promise<void> {
  const results = await piiTransform('encrypt', [{ id: docRef.id, data }]);
  return updateDoc(docRef, results[0]?.data);
}

/**
 * Wraps setDoc, encrypting all PII fields and generating blind
 * indexes (server-side transform).
 */
export async function secureSetDoc(docRef: any, data: any, options?: any): Promise<void> {
  const results = await piiTransform('encrypt', [{ id: docRef.id, data }]);
  return setDoc(docRef, results[0]?.data, options);
}

/**
 * Wraps addDoc, encrypting all PII fields and generating blind
 * indexes (server-side transform).
 */
export async function secureAddDoc(colRef: any, data: any): Promise<any> {
  const results = await piiTransform('encrypt', [{ data }]);
  return addDoc(colRef, results[0]?.data);
}
